import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { TextareaUI } from "ui/textareaUI";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { Checkbox } from "../../ui/checkbox";
import {
  ArrowLeft,
  Upload,
  User,
  FileText,
  Building2,
  DollarSign,
  Wallet,
  File,
} from "lucide-react";
import { toast } from "sonner";
import { getDetailsByITS } from "services/visaService";
import {
  maskMobileNumber,
  maskEmail,
  maskWhatsAppNumber,
} from "../../utils/commonutils";
import {
  EmployeeApi,
  getEmployeeById,
  createEmployee,
  updateEmployee,
  getEmployeePassportDetails,
  createEmployeePassport,
  getEmployeeVisaDetails,
  CreateEmployeeVisaPayload,
  getEmployeeFamilyDetails,
  createEmployeeFamily,
  deleteEmployeeFamily,
  getEmployeeSalary,
  getEmployeeAssignment,
  getEmployeeQarzan,
  getQarzanTypes,
  createEmployeeQarzan,
  CreateEmployeePassportPayload,
  createEmployeeVisa,
  getEmployeeWithDocuments,
  uploadEmployeeDocument,
  rejectEmployeeDocument,
} from "services/employeeService";
import React from "react";
import type {
  PassportDetails,
  VisaDetails,
  FamilyMember,
} from "./components/employee/tabs/PassportFamilyTab";
import PassportFamilyTab from "./components/employee/tabs/PassportFamilyTab";
import type { Department } from "./components/employee/tabs/DepartmentsTab";
import DepartmentsTab from "./components/employee/tabs/DepartmentsTab";
import FacilitiesTab, {
  type Facility,
} from "./components/employee/tabs/FacilitiesTab";
import LoansTab from "./components/employee/tabs/QarsanTab";
import type { Loan } from "./components/employee/tabs/QarsanTab";
import DocumentsTab, {
  EmployeeDocument,
} from "./components/employee/tabs/DocumentsTab";
import { normalizeDate } from "utils/commonutils";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

interface EmployeeFormPageProps {
  onNavigate: (page: string) => void;
  employeeId?: string;
}

export function EmployeeFormPage({
  onNavigate,
  employeeId,
}: EmployeeFormPageProps) {
  const { setPageHeader } = usePageHeader();
  const [activeTab, setActiveTab] = useState("personal");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id } = useParams();
  const location = useLocation();
  const isEditMode = location.pathname.startsWith("/employee-management/edit");
  const isViewMode = location.pathname.startsWith("/employee-management/view");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [employee, setEmployee] = useState<EmployeeApi | null>(null);
  const [loading, setLoading] = useState(false);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [picturePreview, setPicturePreview] = useState("");
  const isAddMode = !isEditMode && !isViewMode;
  const [isFetchingIts, setIsFetchingIts] = useState(false);
  const [isItsVerified, setIsItsVerified] = useState(false);

  const [formData, setFormData] = useState({
    // Personal
    title: "Mr",
    firstName: "",
    middleName: "",
    lastName: "",
    fullName: "",
    arabicName: "",
    dateOfBirth: "",
    gender: "",
    dateOfJoining: "",
    employeeNo: "",
    itsNo: "",
    nonItsEmployee: false,
    hrType: "Staff",
    status: "active",
    roomNo: "",
    jamaaFariqYear: "",
    studiedInJamaaYear: "",
    referredBy: "",
    referrerContact: "",
    // Contact
    mobile: "",
    alternateMobile: "",
    residenceNo: "",
    officeName: "",
    officeNo: "",
    email: "",
    alternateEmail: "",
    whatsapp: "",
    mailingAddress: "",
    permanentAddress: "",
    sameAsMailingAddress: false,
    // Passport & Visa
    passportNameEnglish: "",
    passportNameArabic: "",
    passportNo: "",
    passportIssueDate: "",
    passportExpiryDate: "",
    visaType: "",
    visaNumber: "",
    visaFolderNo: "",
    visaIssuedFrom: "",
    visaIssuedDate: "",
    visaValidity: "",
  });


  useEffect(() => {
    setPageHeader({
      title: isViewMode
        ? "View Employee"
        : isEditMode
          ? "Update Employee"
          : "Add Employee",
      childTitle: "Complete employee information across all tabs",
      icon: <User className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader, isViewMode, isEditMode]);


  const fetchItsDetails = async (itsNo: string) => {
    if (!itsNo || itsNo.length !== 8) {
      setIsItsVerified(false);
      return;
    }

    try {
      setIsFetchingIts(true);

      const info = await getDetailsByITS(itsNo, true);

      // NON ITS USER
      if (!info) {
        setFormData((prev) => ({
          ...prev,
          firstName: "",
          middleName: "",
          lastName: "",
          fullName: "",
          email: "",
          mobile: "",
          whatsapp: "",
          nonItsEmployee: true,
        }));

        setIsItsVerified(false);

        toast.info("No ITS record found. Marked as Non ITS Employee.");

        return;
      }

      // ITS USER FOUND
      const fullName = info.fullName || "";
      const nameParts = fullName.split(" ");

      setFormData((prev) => ({
        ...prev,

        firstName: nameParts[0] || "",
        middleName:
          nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "",

        lastName: nameParts.length > 1 ? nameParts[nameParts.length - 1] : "",

        fullName,

        email: info.email || "",
        mobile: info.mobile_No || "",
        whatsapp: info.whatsApp_No || "",

        nonItsEmployee: false,
      }));

      setIsItsVerified(true);

      toast.success("ITS details fetched successfully");
    } catch (error) {
      console.error(error);

      setFormData((prev) => ({
        ...prev,
        nonItsEmployee: true,
      }));

      setIsItsVerified(false);

      toast.error("Failed to fetch ITS details");
    } finally {
      setIsFetchingIts(false);
    }
  };

  useEffect(() => {
    const loadEmployee = async () => {
      if ((!isEditMode && !isViewMode) || !id) return;
      try {
        setLoading(true);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const employeeData = await getEmployeeById(id!);
        const response = await getEmployeeWithDocuments(employeeData.id);
        const data = response.employee;
        const passportRes = await getEmployeePassportDetails(data.id);

        setPassportDetails(
          passportRes.map((item) => ({
            id: item.id,

            passportNameEn: item.passportNameEn || "",

            passportNameAr: item.passportNameAr || "",

            passportNo: item.passportNo || "",

            issueDate: item.issueDate ? item.issueDate.split("T")[0] : "",

            expiryDate: item.expiryDate ? item.expiryDate.split("T")[0] : "",
          })),
        );

        const visaRes = await getEmployeeVisaDetails(data.id);

        setVisaDetails(
          visaRes.map((item) => ({
            id: item.id,

            visaType: item.visaType || "",

            visaNumber: item.visaNumber || "",

            visaFolderNo: item.visaFolderNo || "",

            issuedFrom: item.issuedFrom || "",

            issuedDate: item.issuedDate ? item.issuedDate.split("T")[0] : "",

            validity: item.validity || "",
          })),
        );

        const familyRes = await getEmployeeFamilyDetails(data.id);
        setFamilyMembers(
          familyRes.map((item) => {
            const today = new Date();
            const dob = new Date(item.dateOfBirth);
            const age = Math.floor(
              (today.getTime() - dob.getTime()) /
                (365.25 * 24 * 60 * 60 * 1000),
            );
            return {
              id: item.id,
              relation: item.relation || "",
              itsId: item.itsId || "",
              name: item.name || "",
              dob: item.dateOfBirth ? item.dateOfBirth.split("T")[0] : "",
              age: age.toString(),
              occupation: item.occupation || "",
              staying: item.stayingCity || "",
            };
          }),
        );
        const salaryRes = await getEmployeeSalary(data.id);

        setFacilities(
          salaryRes.map((item) => ({
            id: item.id.toString(),
            salaryHeadId: item.salaryHeadId,
            currencyHeadId: item.currencyHeadId,
            enabled: item.isActive,
            name: item.salaryHeadName,
            type: item.salaryHeadName.toLowerCase().includes("deduction")
              ? "Deduction"
              : "Earning",

            currency: item.currencyHeadName,
            amount: item.amount,
            remuneration: item.remuneration,
            wefDate: item.withEffectFrom
              ? item.withEffectFrom.split("T")[0]
              : "",

            wetDate: "",
            isSpecial: false,
            leaveCalc: false,
            remarks: "",
            stopped: false,
          })),
        );
        const assignmentRes = await getEmployeeAssignment(data.id);
        const qarzanTypesRes = await getQarzanTypes();

        setDepartments(
          assignmentRes.map((item) => ({
            id: item.id,

            venue: item.venueName || "",

            department: item.departmentName || "",

            designation: item.designationName || "",

            reportingManager: item.reportingManagerName || "",

            subordinateReportingManager:
              item.subordinateReportingManagerName || "",

            venueId: item.venueId || 0,

            departmentId: item.departmentId || 0,

            designationId: item.designationId || 0,

            reportingManagerId: item.reportingManagerId || 0,

            subordinateReportingManagerId:
              item.subordinateReportingManagerId || 0,
          })),
        );
        const qarzanRes = await getEmployeeQarzan(data.id);
        setLoans(
          qarzanRes.map((item) => ({
            id: item.id.toString(),
            type:
              qarzanTypesRes.find((q) => q.id === item.qarzanTypeId)
                ?.qarzanTypeName || "",
            amount: item.amount,
            issuedDate: item.issuedDate ? item.issuedDate.split("T")[0] : "",
            dueDate: item.dueDate ? item.dueDate.split("T")[0] : "",
            remaining: item.remaining,
            status: item.isActive ? "Active" : "Inactive",
          })),
        );

        setEmployee(data);
        setFormData({
          title: data.title || "",
          firstName: data.firstName || "",
          middleName: data.middleName || "",
          lastName: data.lastName || "",
          fullName: data.fullName || "",
          arabicName: data.arabicName || "",
          dateOfBirth: data.dateOfBirth ? data.dateOfBirth.split("T")[0] : "",
          gender: data.gender || "Male",
          dateOfJoining: data.dateOfJoining
            ? data.dateOfJoining.split("T")[0]
            : "",
          employeeNo: data.employeeNo || "",
          itsNo: data.itsNo || "",
          nonItsEmployee: data.isNonIts || false,
          hrType: data.hrType || "Staff",
          status: data.status || "active",
          roomNo: data.roomNo || "",
          jamaaFariqYear: data.jamaaFariqYear?.toString() || "",
          studiedInJamaaYear: data.studiedInJamaaYear?.toString() || "",
          referredBy: data.referredBy || "",
          referrerContact: data.referrerContact || "",
          mobile: data.mobileNo || "",
          alternateMobile: data.alternateMobile || "",
          residenceNo: data.residenceNo || "",
          officeName: data.officeName || "",
          officeNo: data.officeNo || "",
          email: data.email || "",
          alternateEmail: data.alternateEmail || "",
          whatsapp: data.whatsApp || "",
          mailingAddress: data.mailingAddress || "",
          permanentAddress: data.permanentAddress || "",
          sameAsMailingAddress: data.sameAsBilling || false,
          // PASSPORT
          passportNameEnglish: "",
          passportNameArabic: "",
          passportNo: "",
          passportIssueDate: "",
          passportExpiryDate: "",
          visaType: "",
          visaNumber: "",
          visaFolderNo: "",
          visaIssuedFrom: "",
          visaIssuedDate: "",
          visaValidity: "",
        });

        if (data.picturePath) {
          setPicturePreview(
            `http://fhims.najafkarbala.com:5153/${data.picturePath.replace(/^\/+/, "")}`,
          );
        }

        const defaultDocuments: EmployeeDocument[] = [
          {
            id: "passport-copy",
            documentMasterId: 2,
            name: "Passport Copy",
            acceptedFormats: "jpg,png",
            maxFileSizeMB: 10,
            isRequired: true,
            uploadedFile: null,
            uploadedUrl: "",
            history: [],
          },
          {
            id: "visa-copy",
            documentMasterId: 7,
            name: "Visa Copy",
            acceptedFormats: "pdf",
            maxFileSizeMB: 10,
            isRequired: true,
            uploadedFile: null,
            uploadedUrl: "",
            history: [],
          },
          {
            id: "other-document",
            documentMasterId: 6,
            name: "Other Document",
            acceptedFormats: "pdf",
            maxFileSizeMB: 10,
            isRequired: false,
            uploadedFile: null,
            uploadedUrl: "",
            history: [],
          },
        ];

        const uploadedDocuments = response.documents || [];

        const mergedDocuments = defaultDocuments.map((defaultDoc) => {
          const uploadedDoc = uploadedDocuments.find(
            (doc) => doc.documentName === defaultDoc.name,
          );

          if (!uploadedDoc) {
            return defaultDoc;
          }

          return {
            ...defaultDoc,
            documentId: uploadedDoc.documentId,
            documentMasterId: uploadedDoc.documentMasterId,

            uploadedUrl:
              uploadedDoc.versions?.length > 0
                ? `http://fhims.najafkarbala.com:5153${uploadedDoc.versions[0].filePath}`
                : "",

            history:
              uploadedDoc.versions
                ?.flatMap(
                  (version) =>
                    version.histories?.map((history) => ({
                      id: history.approvalHistoryId.toString(),

                      action: history.historyStatus,

                      historyStatus: history.historyStatus,

                      actionAt: history.actionAt
                        ? new Date(history.actionAt).toLocaleString()
                        : "",

                      remarks: version.documentVersionRemarks || "No remarks",
                    })) || [],
                )
                ?.sort(
                  (a, b) =>
                    new Date(b.actionAt).getTime() -
                    new Date(a.actionAt).getTime(),
                ) || [],
          };
        });

        setDocuments(mergedDocuments);
      } catch (error) {
        console.error(error);

        toast.error("Failed to load employee");
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
  }, [id, isEditMode, isViewMode]);

  // Auto-generate full name when first, middle, or last name changes
  useEffect(() => {
    const parts = [
      formData.firstName,
      formData.middleName,
      formData.lastName,
    ].filter(Boolean);

    const generatedFullName = parts.join(" ");

    if (formData.fullName !== generatedFullName) {
      setFormData((prev) => ({
        ...prev,
        fullName: generatedFullName,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.firstName, formData.middleName, formData.lastName]);

  useEffect(() => {
    if (formData.nonItsEmployee) return;

    if (formData.itsNo.length === 8) {
      fetchItsDetails(formData.itsNo);

      // Show all required field validations immediately
      validateAllFields();
    }

    if (formData.itsNo.length !== 8) {
      setIsItsVerified(false);

      // clear validations when ITS becomes invalid
      setFormErrors({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.itsNo, formData.nonItsEmployee]);

  useEffect(() => {
    if (formData.itsNo.length === 8) {
      validateAllFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.firstName,
    formData.lastName,
    formData.dateOfBirth,
    formData.gender,
    formData.dateOfJoining,
    formData.mobile,
    formData.email,
    formData.mailingAddress,
  ]);

  const [passportDetails, setPassportDetails] = useState<PassportDetails[]>([]);
  const [visaDetails, setVisaDetails] = useState<VisaDetails[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);

  const [documents, setDocuments] = useState<EmployeeDocument[]>([
    {
      id: "passport-copy",
      documentMasterId: 2,
      name: "Passport Copy",
      acceptedFormats: "jpg,png",
      maxFileSizeMB: 10,
      isRequired: true,
      uploadedFile: null,
      uploadedUrl: "",
      history: [],
    },
    {
      id: "visa-copy",
      documentMasterId: 7,
      name: "Visa Copy",
      acceptedFormats: "pdf",
      maxFileSizeMB: 10,
      isRequired: true,
      uploadedFile: null,
      uploadedUrl: "",
      history: [],
    },
    {
      id: "other-document",
      documentMasterId: 6,
      name: "Other Document",
      acceptedFormats: "pdf",
      maxFileSizeMB: 10,
      isRequired: false,
      uploadedFile: null,
      uploadedUrl: "",
      history: [],
    },
  ]);

  const savePassport = async (passportData: PassportDetails) => {
    try {
      if (!employee?.id) {
        toast.error("Employee not found");
        return;
      }

      const payload: CreateEmployeePassportPayload = {
        employeeId: employee.id,
        passportNameEn: passportData.passportNameEn,
        passportNameAr: passportData.passportNameAr,
        passportNo: passportData.passportNo,
        issueDate: passportData.issueDate || null,
        expiryDate: passportData.expiryDate || null,
      };

      const res = await createEmployeePassport(payload);

      setPassportDetails((prev) => [
        ...prev,
        {
          id: res.id,
          passportNameEn: res.passportNameEn || "",
          passportNameAr: res.passportNameAr || "",
          passportNo: res.passportNo || "",
          issueDate: res.issueDate ? res.issueDate.split("T")[0] : "",
          expiryDate: res.expiryDate ? res.expiryDate.split("T")[0] : "",
        },
      ]);

      toast.success("Passport added successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save passport");
    }
  };

  const saveVisa = async (visaData: VisaDetails) => {
    try {
      if (!employee?.id) {
        toast.error("Employee not found");
        return;
      }

      const payload: CreateEmployeeVisaPayload = {
        employeeId: employee.id,
        visaType: visaData.visaType,
        visaNumber: visaData.visaNumber,
        visaFolderNo: visaData.visaFolderNo,
        issuedFrom: visaData.issuedFrom,
        issuedDate: visaData.issuedDate || null,
        validity: visaData.validity,
      };

      const res = await createEmployeeVisa(payload);

      setVisaDetails((prev) => [
        ...prev,
        {
          id: res.id,
          visaType: res.visaType || "",
          visaNumber: res.visaNumber || "",
          visaFolderNo: res.visaFolderNo || "",
          issuedFrom: res.issuedFrom || "",
          issuedDate: res.issuedDate ? res.issuedDate.split("T")[0] : "",
          validity: res.validity || "",
        },
      ]);

      toast.success("Visa added successfully");
    } catch (error) {
      console.error(error);
      toast.error("Failed to save visa");
    }
  };

  const saveFamilyMember = async (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    familyData: any,
  ) => {
    try {
      if (!employee?.id) {
        toast.error("Employee not found");
        return;
      }

      const payload = {
        employeeId: employee.id,
        relation: familyData.relation,
        itsId: familyData.itsId,
        name: familyData.name,
        dateOfBirth: familyData.dob,
        occupation: familyData.occupation,
        stayingCity: familyData.staying,
      };

      const res = await createEmployeeFamily(payload);
      const today = new Date();
      const dob = new Date(res.dateOfBirth);
      const age = Math.floor(
        (today.getTime() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000),
      );

      setFamilyMembers((prev) => [
        ...prev,
        {
          id: res.id,
          relation: res.relation,
          itsId: res.itsId,
          name: res.name,
          dob: res.dateOfBirth ? res.dateOfBirth.split("T")[0] : "",
          age: age.toString(),
          occupation: res.occupation,
          staying: res.stayingCity,
        },
      ]);

      toast.success("Family member added successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to save family member");
    }
  };

  const deleteFamilyMember = async (id: number) => {
    try {
      await deleteEmployeeFamily(id);

      setFamilyMembers((prev) => prev.filter((item) => item.id !== id));

      toast.success("Family member deleted successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to delete family member");
    }
  };

  const saveQarzan = async (loanData: Loan) => {
    try {
      if (!employee?.id) {
        toast.error("Employee not found");
        return;
      }
      const qarzanType = (await getQarzanTypes()).find(
        (q) => q.qarzanTypeName === loanData.type,
      );
      if (!qarzanType) {
        toast.error("Invalid Qarzan Type");
        return;
      }
      const payload = {
        employeeId: employee.id,
        qarzanTypeId: qarzanType.id,
        amount: loanData.amount,
        issuedDate: loanData.issuedDate,
        dueDate: loanData.dueDate,
        remaining: loanData.remaining,
        isActive: loanData.status === "Active",
      };
      const res = await createEmployeeQarzan(payload);
      setLoans((prev) => [
        ...prev,
        {
          id: res.id.toString(),
          type: loanData.type,
          amount: res.amount,
          issuedDate: res.issuedDate ? res.issuedDate.split("T")[0] : "",
          dueDate: res.dueDate ? res.dueDate.split("T")[0] : "",
          remaining: res.remaining,
          status: res.isActive ? "Active" : "Inactive",
        },
      ]);

      toast.success("Qarzan added successfully");
    } catch (error) {
      console.error(error);

      toast.error("Failed to save Qarzan");
    }
  };

  const validateField = (fieldName: string, value: string) => {
    const errors = { ...formErrors };

    switch (fieldName) {
      case "mobile":
        if (!isItsVerified) {
          if (!/^\d{10}$/.test(value)) {
            errors.mobile = "Mobile Number must be exactly 10 digits";
          } else {
            delete errors.mobile;
          }
        }
        break;

      case "alternateMobile":
        if (value && !/^\d{10}$/.test(value)) {
          errors.alternateMobile = "Alternate Mobile must be exactly 10 digits";
        } else if (value && value === formData.mobile) {
          errors.alternateMobile =
            "Alternate Mobile must be different from Mobile";
        } else {
          delete errors.alternateMobile;
        }
        break;

      case "whatsapp":
        if (!isItsVerified) {
          if (value && !/^\d{10}$/.test(value)) {
            errors.whatsapp = "WhatsApp Number must be exactly 10 digits";
          } else {
            delete errors.whatsapp;
          }
        }
        break;

      case "officeNo":
        if (value && !/^\d{10}$/.test(value)) {
          errors.officeNo = "Office Number must be exactly 10 digits";
        } else {
          delete errors.officeNo;
        }
        break;

      case "referrerContact":
        if (value && !/^\d{10}$/.test(value)) {
          errors.referrerContact = "Referrer Contact must be exactly 10 digits";
        } else {
          delete errors.referrerContact;
        }
        break;

      case "alternateEmail":
        if (value && value.toLowerCase() === formData.email.toLowerCase()) {
          errors.alternateEmail =
            "Alternate Email must be different from Email";
        } else {
          delete errors.alternateEmail;
        }
        break;

      case "jamaaFariqYear":
        if (value && !/^\d{4}$/.test(value)) {
          errors.jamaaFariqYear = "Jamaa Fariq Year must be exactly 4 digits";
        } else {
          delete errors.jamaaFariqYear;
        }
        break;

      case "studiedInJamaaYear":
        if (value && !/^\d{4}$/.test(value)) {
          errors.studiedInJamaaYear =
            "Studied in Jamaa Year must be exactly 4 digits";
        } else {
          delete errors.studiedInJamaaYear;
        }
        break;
    }

    setFormErrors(errors);
  };

  const validateAllFields = () => {
    const errors: Record<string, string> = {};
    const today = new Date();

    if (!formData.firstName.trim()) errors.firstName = "First Name is required";

    if (!formData.lastName.trim()) errors.lastName = "Last Name is required";

    if (!formData.arabicName.trim())
      errors.arabicName = "Arabic Name is required";

    if (!formData.dateOfBirth) errors.dateOfBirth = "Date of Birth is required";

    if (formData.dateOfBirth && new Date(formData.dateOfBirth) > today) {
      errors.dateOfBirth = "DOB cannot be future date";
    }

    if (!formData.gender) errors.gender = "Gender is required";

    if (!formData.dateOfJoining)
      errors.dateOfJoining = "Date of Joining is required";

    if (formData.dateOfJoining && new Date(formData.dateOfJoining) > today) {
      errors.dateOfJoining = "DOJ cannot be future date";
    }

    if (formData.nonItsEmployee) {
      if (!formData.mobile) {
        errors.mobile = "Mobile Number is required";
      } else if (!/^\d{10}$/.test(formData.mobile)) {
        errors.mobile = "Mobile Number must be exactly 10 digits";
      }
    }

    if (
      formData.alternateMobile &&
      !/^\d{10}$/.test(formData.alternateMobile)
    ) {
      errors.alternateMobile = "Alternate Mobile must be exactly 10 digits";
    }

    if (
      formData.mobile &&
      formData.alternateMobile &&
      formData.mobile === formData.alternateMobile
    ) {
      errors.alternateMobile = "Alternate Mobile must be different from Mobile";
    }

    if (
      formData.nonItsEmployee &&
      formData.whatsapp &&
      !/^\d{10}$/.test(formData.whatsapp)
    ) {
      errors.whatsapp = "WhatsApp Number must be exactly 10 digits";
    }

    if (
      formData.email &&
      formData.alternateEmail &&
      formData.email.toLowerCase() === formData.alternateEmail.toLowerCase()
    ) {
      errors.alternateEmail = "Alternate Email must be different from Email";
    }

    if (!formData.email) errors.email = "Email is required";

    if (!formData.mailingAddress)
      errors.mailingAddress = "Mailing Address is required";

    if (!formData.sameAsMailingAddress && !formData.permanentAddress) {
      errors.permanentAddress = "Permanent Address is required";
    }

    if (formData.jamaaFariqYear && !/^\d{4}$/.test(formData.jamaaFariqYear)) {
      errors.jamaaFariqYear = "Jamaa Fariq Year must be exactly 4 digits";
    }

    if (
      formData.studiedInJamaaYear &&
      !/^\d{4}$/.test(formData.studiedInJamaaYear)
    ) {
      errors.studiedInJamaaYear =
        "Studied in Jamaa Year must be exactly 4 digits";
    }

    if (
      formData.referrerContact &&
      !/^\d{10}$/.test(formData.referrerContact)
    ) {
      errors.referrerContact = "Referrer Contact must be exactly 10 digits";
    }

    setFormErrors(errors);

    return errors;
  };

  const validatePassportTab = () => {
    return true;
  };

  const validateTab = (tab: string) => {
    switch (tab) {
      case "personal":
        return Object.keys(validateAllFields()).length === 0;
      case "passport-family":
        return validatePassportTab();
      default:
        return true;
    }
  };

  const handleSaveEmployee = async () => {
    const isValid = validateTab(activeTab);

    if (!isValid) {
      toast.error("Please fill all required fields", {
        id: "validation-error",
      });

      return;
    }

    try {
      const payload = {
        employeeNo: formData.employeeNo,
        itsNo: formData.itsNo,
        title: formData.title,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        fullName: formData.fullName,
        arabicName: formData.arabicName,
        isNonIts: formData.nonItsEmployee,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        dateOfJoining: formData.dateOfJoining,
        status: formData.status,
        hrType: formData.hrType,
        roomNo: formData.roomNo,
        jamaaFariqYear: Number(formData.jamaaFariqYear),
        studiedInJamaaYear: Number(formData.studiedInJamaaYear),
        referredBy: formData.referredBy,
        referrerContact: formData.referrerContact,
        mobileNo: formData.mobile,
        alternateMobile: formData.alternateMobile,
        residenceNo: formData.residenceNo,
        whatsApp: formData.whatsapp,
        email: formData.email,
        alternateEmail: formData.alternateEmail,
        officeName: formData.officeName,
        officeNo: formData.officeNo,
        mailingAddress: formData.mailingAddress,
        permanentAddress: formData.permanentAddress,
        sameAsBilling: formData.sameAsMailingAddress,
        picture: pictureFile,
      };

      let savedEmployeeId = employee?.id || 0;

      if (isEditMode && employee) {
        await updateEmployee({
          id: employee.id,
          ...payload,
          picturePath: picturePreview || "",
        });

        savedEmployeeId = employee.id;
      } else {
        const createdEmployee = await createEmployee(payload);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        savedEmployeeId = createdEmployee.id;
      }

      toast.success(
        isEditMode
          ? "Employee updated successfully"
          : "Employee created successfully",
      );

      onNavigate("employee-management");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message || "Failed to save employee";

      toast.error(apiMessage);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => onNavigate("employee-management")}
              className="bg-white border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {/* <div>
              <h1
                style={{ color: "#5B9BD5" }}
                className="flex items-center gap-2"
              >
                <User className="w-6 h-6" />
                {isViewMode
                  ? "View Employee"
                  : isEditMode
                    ? "Update Employee"
                    : "Add Employee"}
              </h1>
              <p className="text-gray-600">
                Complete employee information across all tabs
              </p>
            </div> */}
          </div>
        </div>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={(nextTab) => {
            if (isAddMode && nextTab !== "personal") {
              toast.error("Please save employee first to access other tabs");
              return;
            }
            const isValid = validateTab(activeTab);
            if (!isValid) {
              toast.error("Please fill all required fields", {
                id: "validation-error",
              });
              return;
            }
            setActiveTab(nextTab);
          }}
          className="w-full"
          style={{ marginTop: "16px" }}
        >
          <TabsList className="flex w-full h-auto bg-transparent p-0">
            <TabsTrigger
              value="personal"
              className="whitespace-nowrap px-4 py-2 rounded-lg text-gray-500 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-300 data-[state=active]:text-gray-800"
            >
              <User className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Personal</span>
            </TabsTrigger>
            <TabsTrigger
              value="passport-family"
              disabled={isAddMode}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-gray-500 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-300 data-[state=active]:text-gray-800
                ${isAddMode ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <FileText className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Passport & Family</span>
            </TabsTrigger>
            <TabsTrigger
              value="departments"
              disabled={isAddMode}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-gray-500 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-300 data-[state=active]:text-gray-800
                ${isAddMode ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Building2 className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Departments</span>
            </TabsTrigger>
            <TabsTrigger
              value="facilities"
              disabled={isAddMode}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-gray-500 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-300 data-[state=active]:text-gray-800
                ${isAddMode ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <DollarSign className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Salary</span>
            </TabsTrigger>
            <TabsTrigger
              value="loans"
              disabled={isAddMode}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-gray-500 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-300 data-[state=active]:text-gray-800
                ${isAddMode ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <Wallet className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Qarzan</span>
            </TabsTrigger>
            <TabsTrigger
              value="documents"
              disabled={isAddMode}
              className={`whitespace-nowrap px-4 py-2 rounded-lg text-gray-500 data-[state=active]:bg-white data-[state=active]:border data-[state=active]:border-gray-300 data-[state=active]:text-gray-800
                ${isAddMode ? "opacity-50 cursor-not-allowed" : ""}`}
            >
              <File className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Documents</span>
            </TabsTrigger>
          </TabsList>

          {/* Personal Tab */}
          <TabsContent value="personal" style={{ marginTop: "10px" }}>
            <Card className="bg-white">
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Photo Upload + Employee No + Non ITS Checkbox */}
                <div className="flex flex-col md:flex-row items-start gap-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-4">
                      {/* Hidden File Input */}
                      {!isViewMode && (
                        <input
                          type="file"
                          id="employee-photo"
                          accept="image/png,image/jpeg,image/jpg"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;

                            // Validate size (2MB max)
                            if (file.size > 2 * 1024 * 1024) {
                              toast.error("Image size must be less than 2MB");
                              return;
                            }

                            setPictureFile(file);

                            const previewUrl = URL.createObjectURL(file);
                            setPicturePreview(previewUrl);
                          }}
                        />
                      )}

                      {/* Clickable Upload Circle */}
                      <div className="flex flex-col items-center">
                        <div
                          className={`w-24 h-24 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 overflow-hidden relative ${
                            !isViewMode
                              ? "cursor-pointer hover:border-[#5B9BD5]"
                              : ""
                          }`}
                          onClick={() => {
                            if (!isViewMode) {
                              document
                                .getElementById("employee-photo")
                                ?.click();
                            }
                          }}
                        >
                          {picturePreview ? (
                            <img
                              src={picturePreview}
                              alt="Employee"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Upload className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {!isViewMode && (
                          <p className="text-xs text-gray-500 mt-2 text-center">
                            JPG, PNG max 2MB
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <InputFieldset
                        label="Employee No."
                        id="employeeNo"
                        value={formData.employeeNo}
                        disabled
                        readOnly
                        placeholder="Auto-generated"
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="space-y-2">
                        <InputFieldset
                          label="ITS No."
                          id="itsNo"
                          value={formData.itsNo}
                          onChange={(e) => {
                            setIsItsVerified(false);

                            setFormData({
                              ...formData,
                              itsNo: e.target.value.replace(/\D/g, ""),
                            });
                          }}
                          disabled={formData.nonItsEmployee}
                          placeholder="Enter 8-digit ITS number"
                          maxLength={8}
                        />
                        {isFetchingIts && (
                          <p className="text-xs text-[#5B9BD5]">
                            Fetching ITS details...
                          </p>
                        )}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={formData.nonItsEmployee}
                            onCheckedChange={(checked) =>
                              setFormData({
                                ...formData,
                                nonItsEmployee: !!checked,

                                itsNo: checked ? "" : "",
                                firstName: checked ? formData.firstName : "",
                                middleName: checked ? formData.middleName : "",
                                lastName: checked ? formData.lastName : "",
                                fullName: checked ? formData.fullName : "",
                                email: checked ? formData.email : "",
                                mobile: checked ? formData.mobile : "",
                                whatsapp: checked ? formData.whatsapp : "",
                              })
                            }
                          />
                          <span className="text-sm">Non ITS Employee</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <SelectFieldset label="Title" required>
                      <Select
                        value={formData.title}
                        onValueChange={(value) =>
                          setFormData({ ...formData, title: value })
                        }
                      >
                        <SelectTriggerUI id="title">
                          <SelectValue />
                        </SelectTriggerUI>
                        <SelectContent>
                          <SelectItem value="Mr">Mr</SelectItem>
                          <SelectItem value="Mrs">Mrs</SelectItem>
                          <SelectItem value="Dr">Dr</SelectItem>
                          <SelectItem value="Mulla">Mulla</SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="First Name"
                      required
                      id="firstName"
                      value={formData.firstName}
                      disabled={isItsVerified}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          firstName: e.target.value.replace(/[^a-zA-Z\s]/g, ""),
                        });
                        setFormErrors({ ...formErrors, firstName: "" });
                      }}
                      onBlur={(e) => validateField("firstName", e.target.value)}
                      placeholder="Enter first name"
                      className={
                        formErrors.firstName ? "border-red-500 border-1" : ""
                      }
                    />
                    {formErrors.firstName && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.firstName}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Middle Name"
                      id="middleName"
                      value={formData.middleName}
                      disabled={isItsVerified}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          middleName: e.target.value.replace(
                            /[^a-zA-Z\s]/g,
                            "",
                          ),
                        })
                      }
                      placeholder="Enter middle name"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Last Name"
                      required
                      id="lastName"
                      value={formData.lastName}
                      disabled={isItsVerified}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          lastName: e.target.value.replace(/[^a-zA-Z\s]/g, ""),
                        });
                        setFormErrors({ ...formErrors, lastName: "" });
                      }}
                      onBlur={(e) => validateField("lastName", e.target.value)}
                      placeholder="Enter last name"
                      className={
                        formErrors.lastName ? "border-red-500 border-1" : ""
                      }
                    />

                    {formErrors.lastName && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.lastName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Full Name (auto-generated, read-only) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="Full Name"
                      id="fullName"
                      value={formData.fullName}
                      disabled={isItsVerified}
                      readOnly
                      className="bg-gray-50 text-gray-400"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Arabic Name"
                      required
                      id="arabicName"
                      dir="rtl"
                      value={formData.arabicName}
                      disabled={isItsVerified}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          arabicName: e.target.value.replace(
                            /[^a-zA-Z\s]/g,
                            "",
                          ),
                        })
                      }
                      placeholder="أدخل الاسم بالعربية"
                    />
                  </div>
                </div>

                {/* Personal Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="Date of Birth"
                      required
                      type="date"
                      id="dob"
                      value={formData.dateOfBirth}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          dateOfBirth: e.target.value,
                        });
                        setFormErrors({ ...formErrors, dateOfBirth: "" });
                      }}
                      className={
                        formErrors.dateOfBirth ? "border-red-500 border-1" : ""
                      }
                    />

                    {formErrors.dateOfBirth && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.dateOfBirth}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <SelectFieldset label="Gender" required>
                      <Select
                        value={formData.gender}
                        onValueChange={(value) => {
                          setFormData({ ...formData, gender: value });
                          setFormErrors({ ...formErrors, gender: "" }); // clear error
                        }}
                      >
                        <SelectTriggerUI
                          id="gender"
                          className={
                            formErrors.gender ? "border-red-500 border-1" : ""
                          }
                        >
                          <SelectValue />
                        </SelectTriggerUI>

                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>

                    {/* Error message */}
                    {formErrors.gender && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.gender}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Date of Joining"
                      required
                      type="date"
                      id="doj"
                      value={formData.dateOfJoining}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          dateOfJoining: e.target.value,
                        });
                        setFormErrors({ ...formErrors, dateOfJoining: "" }); // clear error
                      }}
                      className={
                        formErrors.dateOfJoining
                          ? "border-red-500 border-1"
                          : ""
                      }
                    />

                    {/* Error message */}
                    {formErrors.dateOfJoining && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.dateOfJoining}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <SelectFieldset label="Status" required>
                      <Select
                        value={formData.status}
                        onValueChange={(value) =>
                          setFormData({ ...formData, status: value })
                        }
                      >
                        <SelectTriggerUI id="status">
                          <SelectValue />
                        </SelectTriggerUI>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="inactive">Inactive</SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  </div>
                </div>

                {/* HR Type and Room Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <SelectFieldset label="HR Type" required>
                      <Select
                        value={formData.hrType}
                        onValueChange={(value) =>
                          setFormData({ ...formData, hrType: value })
                        }
                      >
                        <SelectTriggerUI id="hrType">
                          <SelectValue />
                        </SelectTriggerUI>
                        <SelectContent>
                          <SelectItem value="Staff">Staff</SelectItem>
                          <SelectItem value="Volunteer">Volunteer</SelectItem>
                          <SelectItem value="Mulla">Mulla</SelectItem>
                          <SelectItem value="Contractor">Contractor</SelectItem>
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Room No"
                      id="roomNo"
                      value={formData.roomNo}
                      onChange={(e) =>
                        setFormData({ ...formData, roomNo: e.target.value })
                      }
                      placeholder="e.g., 101"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Jamaa Fariq Year"
                      id="jamaaFariq"
                      value={formData.jamaaFariqYear}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          jamaaFariqYear: e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 4),
                        })
                      }
                      maxLength={4}
                      onBlur={(e) =>
                        validateField("jamaaFariqYear", e.target.value)
                      }
                      placeholder="e.g., 2022"
                    />
                    {formErrors.jamaaFariqYear && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.jamaaFariqYear}
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Studied in Jamaa Year"
                      id="studiedJamaa"
                      value={formData.studiedInJamaaYear}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          studiedInJamaaYear: e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 4),
                        })
                      }
                      maxLength={4}
                      onBlur={(e) =>
                        validateField("studiedInJamaaYear", e.target.value)
                      }
                      placeholder="e.g., 2020"
                    />
                    {formErrors.studiedInJamaaYear && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.studiedInJamaaYear}
                      </p>
                    )}
                  </div>
                </div>

                {/* Referral Information */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="Referred By"
                      id="referredBy"
                      value={formData.referredBy}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          referredBy: e.target.value.replace(
                            /[^a-zA-Z\s]/g,
                            "",
                          ),
                        })
                      }
                      onBlur={(e) =>
                        validateField("referredBy", e.target.value)
                      }
                      placeholder="Enter name"
                    />
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="Referrer Contact"
                      id="referrerContact"
                      value={formData.referrerContact}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          referrerContact: e.target.value.replace(/\D/g, ""),
                        })
                      }
                      maxLength={10}
                      onBlur={(e) =>
                        validateField("referrerContact", e.target.value)
                      }
                      placeholder="Enter phone number"
                    />
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="pt-6 border-t">
                  <h3
                    className="text-lg font-semibold mb-4"
                    style={{ color: "#5B9BD5" }}
                  >
                    Contact Information
                  </h3>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <InputFieldset
                          label=" Mobile No."
                          required
                          id="mobile"
                          value={
                            isItsVerified
                              ? maskMobileNumber(formData.mobile)
                              : formData.mobile
                          }
                          disabled={isItsVerified}
                          onChange={(e) => {
                            setFormData({
                              ...formData,
                              mobile: e.target.value.replace(/\D/g, ""),
                            });
                            setFormErrors({ ...formErrors, mobile: "" }); // clear error
                          }}
                          maxLength={10}
                          onBlur={(e) =>
                            validateField("mobile", e.target.value)
                          }
                          placeholder="Enter Mobile number"
                          className={
                            formErrors.mobile ? "border-red-500 border-1" : ""
                          }
                        />

                        {/* Error message */}
                        {formErrors.mobile && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.mobile}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <InputFieldset
                          label="Alternate Mobile"
                          id="altMobile"
                          value={formData.alternateMobile}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              alternateMobile: e.target.value.replace(
                                /\D/g,
                                "",
                              ),
                            })
                          }
                          maxLength={10}
                          onBlur={(e) =>
                            validateField("alternateMobile", e.target.value)
                          }
                          placeholder="Enter alternate number"
                        />
                        {formErrors.alternateMobile && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.alternateMobile}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <InputFieldset
                          label="Residence No."
                          id="residenceNo"
                          value={formData.residenceNo}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              residenceNo: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          placeholder="Enter residence number"
                        />
                      </div>
                      <div className="space-y-2">
                        <InputFieldset
                          label="WhatsApp"
                          id="whatsapp"
                          value={
                            isItsVerified
                              ? maskWhatsAppNumber(formData.whatsapp)
                              : formData.whatsapp
                          }
                          disabled={isItsVerified}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              whatsapp: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          maxLength={10}
                          placeholder="Enter WhatsApp number"
                        />
                        {formErrors.whatsapp && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.whatsapp}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <InputFieldset
                          label="Email"
                          required
                          type="email"
                          id="email"
                          value={
                            isItsVerified
                              ? maskEmail(formData.email)
                              : formData.email
                          }
                          disabled={isItsVerified}
                          onChange={(e) => {
                            setFormData({ ...formData, email: e.target.value });
                            setFormErrors({ ...formErrors, email: "" }); // clear error
                          }}
                          onBlur={(e) => validateField("email", e.target.value)}
                          placeholder="user@fhims.com"
                          className={
                            formErrors.email ? "border-red-500 border-1" : ""
                          }
                        />

                        {/* Error message */}
                        {formErrors.email && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.email}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <InputFieldset
                          label="Alternate Email"
                          type="email"
                          id="altEmail"
                          value={formData.alternateEmail}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              alternateEmail: e.target.value,
                            })
                          }
                          onBlur={(e) =>
                            validateField("alternateEmail", e.target.value)
                          }
                          placeholder="example@fhims.com"
                        />
                        {formErrors.alternateEmail && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.alternateEmail}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <InputFieldset
                          label="Office Name"
                          id="officeName"
                          value={formData.officeName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              officeName: e.target.value.replace(
                                /[^a-zA-Z\s]/g,
                                "",
                              ),
                            })
                          }
                          placeholder="Enter office name"
                        />
                      </div>
                      <div className="space-y-2">
                        <InputFieldset
                          label="Office No."
                          id="officeNo"
                          value={formData.officeNo}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              officeNo: e.target.value.replace(/\D/g, ""),
                            })
                          }
                          maxLength={10}
                          placeholder="Enter office number"
                        />
                        {formErrors.officeNo && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.officeNo}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <SelectFieldset label="Mailing Address" required>
                          <TextareaUI
                            id="mailingAddress"
                            value={formData.mailingAddress}
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                mailingAddress: e.target.value,
                              });
                              setFormErrors({
                                ...formErrors,
                                mailingAddress: "",
                              }); // clear error
                            }}
                            placeholder="Street address, City, Country"
                            rows={3}
                            className={
                              formErrors.mailingAddress
                                ? "border-red-500 border-1"
                                : ""
                            }
                          />
                        </SelectFieldset>

                        {/* Error message */}
                        {formErrors.mailingAddress && (
                          <p className="text-red-500 text-sm mt-1">
                            {formErrors.mailingAddress}
                          </p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <SelectFieldset label="Permanent Address" required>
                          <TextareaUI
                            id="permanentAddress"
                            value={
                              formData.sameAsMailingAddress
                                ? formData.mailingAddress
                                : formData.permanentAddress
                            }
                            onChange={(e) => {
                              setFormData({
                                ...formData,
                                permanentAddress: e.target.value,
                              });
                              setFormErrors({
                                ...formErrors,
                                permanentAddress: "",
                              }); // clear error
                            }}
                            placeholder="Street address, City, Country"
                            rows={3}
                            disabled={formData.sameAsMailingAddress}
                            className={
                              formErrors.permanentAddress &&
                              !formData.sameAsMailingAddress
                                ? "border-red-500 border-1"
                                : ""
                            }
                          />
                        </SelectFieldset>
                        {/* Error message */}
                        {formErrors.permanentAddress &&
                          !formData.sameAsMailingAddress && (
                            <p className="text-red-500 text-sm mt-1">
                              {formErrors.permanentAddress}
                            </p>
                          )}
                      </div>
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.sameAsMailingAddress}
                        onCheckedChange={(checked) =>
                          setFormData({
                            ...formData,
                            sameAsMailingAddress: !!checked,
                            permanentAddress: checked
                              ? formData.mailingAddress
                              : formData.permanentAddress,
                          })
                        }
                      />
                      <span className="text-sm">Same as Mailing Address</span>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Form Footer */}
            {!isViewMode && (
              <div className="flex flex-col sm:flex-row justify-end gap-3 sticky bottom-0 bg-white p-4 border-t mt-6">
                <Button
                  variant="outline"
                  onClick={() => onNavigate("employee-management")}
                  className="bg-white w-full sm:w-auto"
                >
                  Cancel
                </Button>
                <Button
                  style={{ background: "#5B9BD5" }}
                  className="text-white w-full sm:w-auto"
                  onClick={handleSaveEmployee}
                >
                  Save
                </Button>
              </div>
            )}
          </TabsContent>

          {/* Passport & Family Tab */}
          <TabsContent value="passport-family">
            <PassportFamilyTab
              passportDetails={passportDetails}
              setPassportDetails={setPassportDetails}
              visaDetails={visaDetails}
              setVisaDetails={setVisaDetails}
              savePassport={savePassport}
              saveVisa={saveVisa}
              familyMembers={familyMembers}
              setFamilyMembers={setFamilyMembers}
              saveFamilyMember={saveFamilyMember}
              deleteFamilyMember={deleteFamilyMember}
              isViewMode={isViewMode}
            />
          </TabsContent>

          {/* Departments Tab */}
          <TabsContent value="departments">
            <DepartmentsTab
              isViewMode={isViewMode}
              employeeId={employee?.id || 0}
              departments={departments}
              setDepartments={setDepartments}
            />
          </TabsContent>

          {/* Facilities (Salary) Tab */}
          <TabsContent value="facilities">
            <FacilitiesTab
              isViewMode={isViewMode}
              employeeId={employee?.id || 0}
              facilities={facilities}
              setFacilities={setFacilities}
            />
          </TabsContent>

          {/* Loans Tab */}
          <TabsContent value="loans">
            <LoansTab
              loans={loans}
              isViewMode={isViewMode}
              setLoans={setLoans}
              saveQarzan={saveQarzan}
            />
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <DocumentsTab
              documents={documents}
              setDocuments={setDocuments}
              isViewMode={isViewMode}
              employeeId={employee?.id}
              onUploadDocument={async (documentMasterId, file) => {
                if (!employee?.id) return null;

                const response = await uploadEmployeeDocument({
                  employeeId: employee.id,
                  documentMasterId,
                  file,
                });

                return response;
              }}
              onDeleteDocument={async (documentId, remarks) => {
                const response = await rejectEmployeeDocument(
                  documentId,
                  remarks,
                );

                return response;
              }}
              onPassportOCR={(ocrData) => {
                setPassportDetails((prev) => {
                  const passport = {
                    id: Date.now(),

                    passportNameEn:
                      `${ocrData.firstName ?? ""} ${ocrData.middleName ?? ""} ${ocrData.lastName ?? ""}`
                        .replace(/\s+/g, " ")
                        .trim(),

                    passportNameAr: "",

                    passportNo: ocrData.passportNumber ?? "",

                    issueDate: normalizeDate(ocrData.issueDate) ?? "",

                    expiryDate: normalizeDate(ocrData.expiryDate) ?? "",
                  };

                  if (prev.length === 0) {
                    return [passport];
                  }

                  return prev.map((item, index) =>
                    index === 0
                      ? {
                          ...item,
                          ...passport,
                          id: item.id,
                        }
                      : item,
                  );
                });
              }}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
